import { Typography, type TypographyProps } from '@mui/material';
import { forwardRef, type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { color } from '@/colors';
import { setForwardedRef } from '@/utils/refs';

const EDIT_OUTLINE = `1px solid ${color.White64_T}`;

interface EditableLabelProps {
  value: string;
  onSubmit: (label: string) => void;
  /** When false, no hover tint (e.g. label on a badge that already has hover styling). Default true uses White08_T. */
  showHoverBackground?: boolean;
  /** When true, no border (e.g. when parent badge has its own border) */
  noBorder?: boolean;
  /** When true, opens in edit mode (e.g. from menu "Rename") */
  open?: boolean;
  /** Called when editing ends (blur or submit) */
  onClose?: () => void;
  /**
   * Notified when the user enters or leaves edit mode. Internal `editing` state remains the source of truth;
   * use this for parent side effects (e.g. `nodrag` class, outline, suppressing tooltips).
   */
  onEditingChange?: (editing: boolean) => void;
}

export const EditableLabel = forwardRef<HTMLElement, EditableLabelProps & Omit<TypographyProps, 'onSubmit'>>(
  (
    { value, onSubmit, showHoverBackground = true, noBorder, open, onClose, onEditingChange, ...extraProps },
    forwardedRef,
  ) => {
    const [editing, setEditing] = useState(open ?? false);
    // Internal DOM handle (same element we assign to `forwardedRef`). Read only `rootRef` in handlers—`forwardedRef` may be a callback ref.
    const rootRef = useRef<HTMLElement | null>(null);

    // Merge internal + forwarded DOM ref here—not useImperativeHandle, which is for custom imperative APIs and resyncs poorly when Typography remounts (e.g. key toggles with editing).
    const setRootRef = useCallback(
      (node: HTMLElement | null) => {
        rootRef.current = node;
        setForwardedRef(forwardedRef, node);
      },
      [forwardedRef],
    );

    const setEditingSynced = useCallback(
      (next: boolean) => {
        setEditing(next);
        onEditingChange?.(next);
      },
      [onEditingChange],
    );

    useEffect(() => {
      if (open === true) {
        setEditingSynced(true);
      }
    }, [open, setEditingSynced]);

    const handleSubmit = useCallback(
      (newValue: string) => {
        const trimmed = newValue.trim();
        if (trimmed !== '') {
          onSubmit(trimmed);
        }
      },
      [onSubmit],
    );

    const handleBlur = useCallback(() => {
      const node = rootRef.current;
      const text = node != null ? (node.textContent ?? '') : '';
      handleSubmit(text);
      setEditingSynced(false);
      onClose?.();
    }, [handleSubmit, onClose, setEditingSynced]);

    const handleKeyDown = useCallback(
      (evt: KeyboardEvent<HTMLElement>) => {
        if (evt.code === 'Enter') {
          evt.preventDefault();
          if (!rootRef.current) return;
          handleSubmit(rootRef.current.textContent || '');
          setEditingSynced(false);
          onClose?.();
        } else if (evt.code === 'Escape') {
          evt.preventDefault();
          // press Escape -> cancel edit
          if (!rootRef.current) return;
          rootRef.current.textContent = value;
          setEditingSynced(false);
          onClose?.();
        } else if (evt.code === 'KeyZ' && (evt.getModifierState('Control') || evt.getModifierState('Meta'))) {
          // stop undo propagation while editing (Ctrl/Cmd+Z)
          evt.stopPropagation();
          evt.preventDefault();
        } else if (evt.code === 'Backspace' || evt.code === 'Delete') {
          evt.stopPropagation();
        }
      },
      [handleSubmit, value, onClose, setEditingSynced],
    );

    const handleDoubleClick = useCallback(() => {
      setEditingSynced(true);
    }, [setEditingSynced]);

    // Handle text selection when entering edit mode
    useEffect(() => {
      if (editing && rootRef.current) {
        rootRef.current.focus();

        const range = document.createRange();
        range.selectNodeContents(rootRef.current);

        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }, [editing]);

    return (
      <Typography
        key={editing ? 'editing' : 'display'}
        ref={setRootRef}
        contentEditable={editing}
        suppressContentEditableWarning
        onDoubleClick={handleDoubleClick}
        onBlur={editing ? handleBlur : undefined}
        onKeyDown={editing ? handleKeyDown : undefined}
        {...extraProps}
        sx={{
          ...extraProps.sx,
          cursor: editing ? 'text' : 'pointer',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          padding: '2px 4px',
          borderRadius: 1,
          border: 'none',
          ...(!noBorder && editing && { outline: EDIT_OUTLINE }),
          minHeight: '1.2em',
          ...(!editing &&
            showHoverBackground && {
              '&:hover': {
                backgroundColor: color.White08_T,
              },
            }),
          ...(editing && {
            textOverflow: 'clip',
            '&:focus': {
              outline: noBorder ? 'none' : EDIT_OUTLINE,
            },
            '&::selection': {
              backgroundColor: color.TextSelection,
            },
          }),
        }}
      >
        {value}
      </Typography>
    );
  },
);

EditableLabel.displayName = 'EditableLabel';
